import React from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames/bind';

import recursiveCloneElement from '@ott/helper-recursive-clone-element';

import Position from '@ott/position';
import Portal from '@ott/portal';

import { container as getContainer } from '@ott/utility-overlay';

/*
 * fork of react-overlays/overlay
 * */
class Overlay extends React.PureComponent {
  constructor(props, context) {
    super(props, context);

    this.handleHidden = this.handleHidden.bind(this);

    this.state = {
      exited: !props.show,
    };
  }

  componentDidMount() {
    this.updateContainers();
  }

  componentWillReceiveProps(nextProps) {
    this._needsFlush = true;

    if (nextProps.show) {
      this.setState({ exited: false });
    } else if (!nextProps.transition) {
      // Otherwise let handleHidden take care of marking exited.
      this.setState({ exited: true });
    }
  }

  componentDidUpdate() {
    if (this._needsFlush) {
      this._needsFlush = false;
      this.updateContainers();
    }
  }

  updateContainers() {
    let { container, content } = this.props;

    if (content == null) {
      content = container;
    }

    this.setState(
      {
        container: getContainer(container, document.body),
        content: getContainer(content, document.body),
      },
      () => {
        if (!(this.state.container || this.state.content)) {
          if (this._once) {
            return;
          }
          this._once = true;
          setTimeout(this.updateContainers.bind(this), 0);
        }
      }
    );
  }

  handleHidden(...args) {
    this.setState({ exited: true });

    if (this.props.onExited) {
      this.props.onExited(...args);
    }
  }

  getPortalStyles() {
    let styles = { position: 'static' };
    const { isAbsolutePosition, isRelativePosition, placement } = this.props;
    if (isAbsolutePosition) {
      styles = {
        ...styles,
        position: 'absolute',
      };
      if (placement === 'right') {
        styles.right = '0px';
      }
    }
    if (isRelativePosition) {
      styles = {
        ...styles,
        position: 'relative',
      };
    }
    return styles;
  }

  render() {
    let {
      children,
      containerPadding,
      targetFn,
      placement,
      skipPositionHOC,
      isCentering,
      isFullScreen,
      transition: compTransition,
      ...props
    } = this.props;

    // Don't un-render the overlay while it's transitioning out.
    const mountOverlay = props.show || (compTransition && !this.state.exited);
    if (!mountOverlay || !(this.state.container && this.state.content)) {
      // Don't bother showing anything if we don't have to.
      return null;
    }

    let child = recursiveCloneElement(children);

    if (!skipPositionHOC && !isFullScreen) {
      // Position is be inner-most because it adds inline styles into the child,
      // which the other wrappers don't forward correctly.
      child = (
        <Position
          {...{ containerPadding, targetFn, placement, isCentering }}
          container={this.state.content}
        >
          {child}
        </Position>
      );
    }

    if (compTransition) {
      let { onExit, onExiting, onEnter, onEntering, onEntered } = props;

      // This animates the child node by injecting props, so it must precede
      // anything that adds a wrapping div.
      child = (
        <compTransition
          in={props.show}
          transitionAppear
          onExit={onExit}
          onExiting={onExiting}
          onExited={this.handleHidden}
          onEnter={onEnter}
          onEntering={onEntering}
          onEntered={onEntered}
        >
          {child}
        </compTransition>
      );
    }

    /*   // This goes after everything else because it adds a wrapping div.
     if (rootClose) {
     child = (
     <RootCloseWrapper onRootClose={props.onHide}>
     {child}
     </RootCloseWrapper>
     );
     }*/

    return (
      <Portal
        container={this.state.container}
        isOpen
        zIndex={isFullScreen ? 10000000 : null} // если открываем overlay через modal
        onClose={props.onHide}
        style={this.getPortalStyles()}
      >
        {child}
      </Portal>
    );
  }
}

Overlay.propTypes = {
  ...require('@ott/portal').propTypes,
  ...require('@ott/position').propTypes,
  /**
   * Set the visibility of the Overlay
   */
  show: PropTypes.bool,
  /**
   * Specify whether the overlay should trigger onHide when the user clicks outside the overlay
   */
  rootClose: PropTypes.bool,
  /**
   * A Callback fired by the Overlay when it wishes to be hidden.
   */
  onHide: PropTypes.func,

  /**
   * A `<Transition/>` component used to animate the overlay changes visibility.
   */
  transition: PropTypes.element,

  /**
   * Callback fired before the Overlay transitions in
   */
  onEnter: PropTypes.func,

  /**
   * Callback fired as the Overlay begins to transition in
   */
  onEntering: PropTypes.func,

  /**
   * Callback fired after the Overlay finishes transitioning in
   */
  onEntered: PropTypes.func,

  /**
   * Callback fired right before the Overlay transitions out
   */
  onExit: PropTypes.func,

  /**
   * Callback fired as the Overlay begins to transition out
   */
  onExiting: PropTypes.func,

  /**
   * Callback fired after the Overlay finishes transitioning out
   */
  onExited: PropTypes.func,

  /**
   * Делает позиционирование абсолютным
   */
  isAbsolutePosition: PropTypes.bool,
  isRelativePosition: PropTypes.bool,
  skipPositionHOC: PropTypes.bool,
  isFullScreen: PropTypes.bool,

  // Делает позиционирование с выравниванием по центру по горизонтали, если false то выравнивание по левому краю контенера
  isCentering: PropTypes.bool,
};

Overlay.defaultProps = {
  skipPositionHOC: false,
  isCentering: true,
  isFullScreen: false,
  isRelativePosition: false,
};

export default Overlay;
